import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import AdminHeader from "../../../../components/AdminHeader";
import AdminPageHeader from "../../../../components/admin/AdminPageHeader";
import apiFetch from "../../../../utils/apiFetch";
import { adminListMedia } from "../../../../services/admin";

const BLOCK_TYPES = [
  { value: "text", label: "Texto" },
  { value: "audio", label: "Audio" },
  { value: "video", label: "Video" },
  { value: "download", label: "Descargable" },
];

const BLOCK_PRESETS = [
  {
    value: "momento_de_verdad",
    label: "1. Momentos de verdad",
    type: "audio",
    title: "1. MOMENTOS DE VERDAD",
    position: 1,
  },
  {
    value: "alineamiento_interno",
    label: "2. Alineamiento interno",
    type: "text",
    title: "2. ALINEAMIENTO INTERNO",
    position: 2,
  },
  {
    value: "poder_ejecucion",
    label: "3. Toma acción",
    type: "text",
    title: "3. TOMA ACCIÓN",
    position: 3,
    hasMicroMission: true,
  },
  {
    value: "vida_reino",
    label: "4. Vida de Reino",
    type: "download",
    title: "4. VIDA DE REINO",
    position: 4,
  },
  {
    value: "reflexion",
    label: "5. Reflexión",
    type: "audio",
    title: "5. REFLEXIÓN",
    position: 5,
  },
  {
    value: "custom",
    label: "Bloque personalizado",
    type: "text",
    title: "",
    position: null,
  },
];

const LINE_TYPES = [
  { value: "none", label: "Ninguno" },
  { value: "paragraph", label: "Párrafo" },
  { value: "dash", label: "Rayita —" },
  { value: "dot", label: "Punto •" },
  { value: "check", label: "Check ✓" },
  { value: "number", label: "Numerada 1." },
  { value: "quote", label: "Frase destacada" },
];

const ICON_STYLES = [
  { value: "none", label: "Sin ícono" },
  { value: "book", label: "Libro" },
  { value: "check", label: "Check" },
  { value: "arrow", label: "Flecha" },
  { value: "light", label: "Luz" },
  { value: "heart", label: "Corazón" },
  { value: "crown", label: "Corona" },
  { value: "pen", label: "Lápiz" },
];

const EMPHASIS_STYLES = [
  { value: "none", label: "Sin destaque" },
  { value: "bold", label: "Frase en negrita" },
  { value: "quote", label: "Frase destacada" },
];

function makeLine(overrides = {}) {
  return {
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    kind: "paragraph",
    icon: "none",
    text: "",
    ...overrides,
  };
}

function makeMissionBlock(overrides = {}) {
  return {
    id: crypto.randomUUID(),
    kind: "text",
    prompt: "",
    instruction: "",
    required: false,
    options: [],
    maxSelections: 0,
    min: 1,
    max: 5,
    minLabel: "",
    maxLabel: "",
    allowOther: false,
    allowNone: false,
    allowUnsure: false,
    allowSkip: false,
    journalLabel: "",
    seconds: 60,
    ...overrides,
  };
}

function makeMissionStep(number, overrides = {}) {
  return {
    id: crypto.randomUUID(),
    number,
    title: `Paso ${number}`,
    blocks: [makeMissionBlock()],
    ...overrides,
  };
}

function normalizeMissionOption(option) {
  if (typeof option === "string") {
    return {
      id: crypto.randomUUID(),
      value: option,
      label: option,
      description: "",
    };
  }

  const label = String(
    option?.label || option?.text || option?.value || "",
  ).trim();

  return {
    id: option?.id || crypto.randomUUID(),
    value: option?.value ?? label,
    label,
    description: option?.description || "",
  };
}

function splitMissionOptions(text = "") {
  return String(text || "")
    .split(/\r?\n+/)
    .map((value) => value.trim())
    .filter(Boolean)
    .map(normalizeMissionOption);
}

function normalizeMissionBlock(block = {}) {
  const kindMap = {
    question: "short_text",
    response: "long_text",
    binary_question: "binary",
    multi_select: "multi_choice",
    checklist: "multi_choice",
    selection: "single_choice",
    example: "single_choice",
  };
  const kind = kindMap[block.kind] || block.kind || "text";
  const options = Array.isArray(block.options)
    ? block.options.map(normalizeMissionOption)
    : ["single_choice", "multi_choice"].includes(kind)
      ? splitMissionOptions(block.text)
      : [];

  return makeMissionBlock({
    ...block,
    kind,
    prompt: block.prompt || block.text || "",
    instruction: block.instruction || block.placeholder || "",
    required:
      typeof block.required === "boolean"
        ? block.required
        : [
            "single_choice",
            "multi_choice",
            "binary",
            "scale",
            "short_text",
            "long_text",
            "complete_phrase",
            "confirmation",
          ].includes(kind),
    options,
  });
}

function normalizeMissionStepForEditor(step = {}, stepIndex = 0) {
  const sourceBlocks = Array.isArray(step.blocks) ? step.blocks : [];
  const blocks = [];

  for (let index = 0; index < sourceBlocks.length; index += 1) {
    if (
      ["example", "multi_select", "checklist"].includes(
        sourceBlocks[index]?.kind,
      )
    ) {
      const normalized = normalizeMissionBlock(sourceBlocks[index]);
      const previous = blocks[blocks.length - 1];

      if (previous?.kind === "text") {
        normalized.prompt = previous.prompt;
        blocks.pop();
      } else {
        normalized.prompt =
          sourceBlocks[index]?.placeholder ||
          (sourceBlocks[index]?.kind === "example"
            ? "Selecciona la opción que mejor representa tu respuesta."
            : "Selecciona las opciones que correspondan.");
      }

      blocks.push(normalized);
      continue;
    }

    if (sourceBlocks[index]?.kind !== "choice_cards") {
      const normalized = normalizeMissionBlock(sourceBlocks[index]);
      blocks.push(normalized);

      if (sourceBlocks[index]?.kind === "text") {
        const timerMatch = String(sourceBlocks[index]?.text || "").match(
          /temporizador[^\d]{0,30}(\d+)\s*(segundos?|minutos?)/i,
        );

        if (timerMatch) {
          const amount = Number(timerMatch[1]);
          const isMinutes = timerMatch[2].toLowerCase().startsWith("minuto");

          blocks.push(
            makeMissionBlock({
              kind: "timer",
              prompt: "Tiempo de pausa guiada",
              seconds: Math.min(
                1800,
                Math.max(15, isMinutes ? amount * 60 : amount),
              ),
            }),
          );
        }
      }

      continue;
    }

    const cards = [];
    let cursor = index;

    while (sourceBlocks[cursor]?.kind === "choice_cards") {
      cards.push(sourceBlocks[cursor]);
      cursor += 1;
    }

    const previous = blocks[blocks.length - 1];
    const prompt =
      previous?.kind === "text"
        ? previous.prompt
        : "Selecciona la opción que mejor representa tu respuesta.";

    if (previous?.kind === "text") blocks.pop();

    blocks.push(
      makeMissionBlock({
        kind: "single_choice",
        prompt,
        required: true,
        options: cards.map((card) =>
          normalizeMissionOption({
            id: card.id,
            value: card.text,
            label: card.text,
          }),
        ),
      }),
    );

    index = cursor - 1;
  }

  return {
    id: step.id || crypto.randomUUID(),
    number: stepIndex + 1,
    title: step.title || `Paso ${stepIndex + 1}`,
    description: step.description || "",
    blocks: blocks.length ? blocks : [makeMissionBlock()],
  };
}

function normalizeMissionForEditor(mission = {}) {
  const sourceSteps = Array.isArray(mission.steps) ? mission.steps : [];

  return {
    kind: "micro_mission",
    version: 2,
    title: mission.title || "TOMA ACCIÓN",
    intro:
      mission.intro ||
      "Trabaja una decisión concreta a tu ritmo. Tus respuestas se guardarán en Bitácora.",
    steps: sourceSteps.length
      ? sourceSteps.map(normalizeMissionStepForEditor)
      : [makeMissionStep(1)],
    closing: {
      title:
        Number(mission.version || 1) >= 2
          ? mission.closing?.title || "Tu recorrido de hoy"
          : "Tu recorrido de hoy",
      message:
        mission.closing?.message ||
        "Tu trabajo quedó guardado. Puedes revisarlo cuando quieras en Bitácora.",
    },
  };
}

function inferPresetFromTitle(title = "") {
  const clean = title.trim().toUpperCase();

  const match = BLOCK_PRESETS.find(
    (preset) => preset.title?.trim().toUpperCase() === clean,
  );

  return match?.value || "custom";
}

function parseLineToken(line) {
  const clean = line.trim();

  if (!clean) return null;

  const iconRegex = /\[\[ICON:(book|check|arrow|light|heart|crown|pen)\]\]\s*/;

  if (clean.startsWith("[[QUOTE]]")) {
    let text = clean.replace("[[QUOTE]]", "").trim();
    let icon = "none";

    const iconMatch = text.match(iconRegex);

    if (iconMatch) {
      icon = iconMatch[1];
      text = text.replace(iconRegex, "").trim();
    }

    return makeLine({
      kind: "quote",
      icon,
      text,
    });
  }

  const bulletMatch = clean.match(/^\[\[BULLET:(dot|check|dash|number)\]\]\s*/);

  if (bulletMatch) {
    let text = clean.replace(/^\[\[BULLET:(dot|check|dash|number)\]\]\s*/, "");
    let icon = "none";

    const iconMatch = text.match(iconRegex);

    if (iconMatch) {
      icon = iconMatch[1];
      text = text.replace(iconRegex, "").trim();
    }

    return makeLine({
      kind: bulletMatch[1],
      icon,
      text: text.trim(),
    });
  }

  let text = clean;
  let icon = "none";

  const iconMatch = text.match(iconRegex);

  if (iconMatch) {
    icon = iconMatch[1];
    text = text.replace(iconRegex, "").trim();

    return makeLine({
      kind: "none",
      icon,
      text,
    });
  }

  return makeLine({
    kind: "paragraph",
    icon: "none",
    text,
  });
}

function parseExistingContent(content = "", preset = "custom") {
  const normalized = String(content || "")
    .replace(/\r\n/g, "\n")
    .trim();

  const parsed = {
    bodyText: "",
    scriptureReference: "",
    scriptureText: "",
    applicationText: "",
    closingText: "",
    resourceNote: "",
    emphasisStyle: "bold",
    lines: [],
  };

  if (!normalized) return parsed;

  const sections = normalized
    .split(/\n\s*---+\s*\n/g)
    .map((section) => section.trim())
    .filter(Boolean);

  if (preset === "momento_de_verdad") {
    const scriptureSection = sections[0] || "";
    const applicationSection = sections[1] || "";

    const scriptureLines = scriptureSection
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

    if (scriptureLines[0]?.startsWith("[[SCRIPTURE_REFERENCE]]")) {
      parsed.scriptureReference = scriptureLines[0]
        .replace("[[SCRIPTURE_REFERENCE]]", "")
        .trim();

      parsed.scriptureText = scriptureLines.slice(1).join("\n\n").trim();
    } else {
      parsed.scriptureText = scriptureSection;
    }

    const appLines = applicationSection
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

    const lastLine = appLines[appLines.length - 1] || "";

    if (lastLine.startsWith("[[QUOTE]]")) {
      parsed.emphasisStyle = "quote";
      parsed.closingText = lastLine.replace("[[QUOTE]]", "").trim();
      parsed.applicationText = appLines.slice(0, -1).join("\n\n").trim();
    } else if (lastLine.startsWith("**") && lastLine.endsWith("**")) {
      parsed.emphasisStyle = "bold";
      parsed.closingText = lastLine.slice(2, -2).trim();
      parsed.applicationText = appLines.slice(0, -1).join("\n\n").trim();
    } else {
      parsed.applicationText = applicationSection.trim();
    }

    return parsed;
  }

  const lines = normalized
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  const working = [...lines];
  const lastLine = working[working.length - 1] || "";

  if (lastLine.startsWith("[[QUOTE]]")) {
    parsed.emphasisStyle = "quote";
    parsed.closingText = lastLine.replace("[[QUOTE]]", "").trim();
    working.pop();
  } else if (lastLine.startsWith("**") && lastLine.endsWith("**")) {
    parsed.emphasisStyle = "bold";
    parsed.closingText = lastLine.slice(2, -2).trim();
    working.pop();
  }

  const paragraphBuffer = [];
  const structuredLines = [];

  working.forEach((line) => {
    if (line.startsWith("[[BULLET:") || line.startsWith("[[QUOTE]]")) {
      structuredLines.push(parseLineToken(line));
      return;
    }

    const parsedLine = parseLineToken(line);

    if (parsedLine) {
      structuredLines.push(parsedLine);
    } else {
      paragraphBuffer.push(line);
    }
  });

  parsed.bodyText = paragraphBuffer.join("\n\n").trim();
  parsed.lines = structuredLines.filter(Boolean);

  return parsed;
}

export default function AdminItems() {
  const { systemId, sectionId, lessonId } = useParams();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [mediaItems, setMediaItems] = useState([]);
  const [mediaSearch, setMediaSearch] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editingPosition, setEditingPosition] = useState(null);

  const [preset, setPreset] = useState("custom");
  const [type, setType] = useState("text");
  const [title, setTitle] = useState("");

  const [hasMicroMission, setHasMicroMission] = useState(false);
  const [missionButtonLabel, setMissionButtonLabel] =
    useState("Pasar a la acción");
  const [showMissionEditor, setShowMissionEditor] = useState(false);
  const [missionTitle, setMissionTitle] = useState("TOMA ACCIÓN");
  const [missionIntro, setMissionIntro] = useState(
    "Trabaja una decisión concreta a tu ritmo. Tus respuestas se guardarán en Bitácora.",
  );

  const [bodyText, setBodyText] = useState("");
  const [scriptureReference, setScriptureReference] = useState("");
  const [scriptureText, setScriptureText] = useState("");
  const [applicationText, setApplicationText] = useState("");
  const [closingText, setClosingText] = useState("");
  const [resourceNote, setResourceNote] = useState("");
  const [emphasisStyle, setEmphasisStyle] = useState("bold");

  const [lines, setLines] = useState([makeLine()]);

  const [missionSteps, setMissionSteps] = useState([makeMissionStep(1)]);

  const [missionClosing, setMissionClosing] = useState({
    title: "Tu recorrido de hoy",
    message:
      "Tu trabajo quedó guardado. Puedes revisarlo cuando quieras en Bitácora.",
    blocks: [],
  });

  const [selectedMedia, setSelectedMedia] = useState(null);
  const [manualMediaUrl, setManualMediaUrl] = useState("");

  async function loadItems() {
    try {
      const data = await apiFetch(
        `/api/systems/${systemId}/sections/${sectionId}/lessons/${lessonId}/items`,
      );
      setItems(data.items || []);
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadMedia(searchValue = "") {
    try {
      const data = await adminListMedia(searchValue);
      setMediaItems(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      console.error("cannot load media", e);
    }
  }

  useEffect(() => {
    loadItems();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadMedia(mediaSearch);
    }, 250);

    return () => clearTimeout(timer);
  }, [mediaSearch]);

  const filteredMedia = useMemo(() => {
    const q = mediaSearch.trim().toLowerCase();

    return mediaItems.filter((item) => {
      if (!q) return true;

      return (
        item.title?.toLowerCase().includes(q) ||
        item.type?.toLowerCase().includes(q) ||
        item.provider?.toLowerCase().includes(q)
      );
    });
  }, [mediaItems, mediaSearch]);

  function getNumberFromTitle(value = "") {
    const match = String(value)
      .trim()
      .match(/^(\d+)[\.\)]?\s+/);
    return match ? Number(match[1]) : null;
  }

  function getPresetPosition() {
    const selected = BLOCK_PRESETS.find((item) => item.value === preset);
    return selected?.position || null;
  }

  function getNextPosition() {
    const positions = items
      .map((item) => Number(item.position))
      .filter((value) => Number.isFinite(value));

    if (!positions.length) return 1;

    return Math.max(...positions) + 1;
  }

  function getPositionForPayload() {
    const numberFromTitle = getNumberFromTitle(title);

    if (numberFromTitle) return numberFromTitle;

    const presetPosition = getPresetPosition();

    if (!editingId && presetPosition) return presetPosition;

    if (editingId && editingPosition) return editingPosition;

    return getNextPosition();
  }

  function getDefaultMission() {
    return {
      kind: "micro_mission",
      version: 2,
      title: "TOMA ACCIÓN",
      intro:
        "Trabaja una decisión concreta a tu ritmo. Tus respuestas se guardarán en Bitácora.",
      steps: [makeMissionStep(1)],
      closing: {
        title: "Tu recorrido de hoy",
        message:
          "Tu trabajo quedó guardado. Puedes revisarlo cuando quieras en Bitácora.",
        blocks: [],
      },
    };
  }

  function parseLessonBlockContent(content = "") {
    const raw = String(content || "");

    try {
      const parsed = JSON.parse(raw);

      if (parsed?.kind === "lesson_block") {
        return {
          body: parsed.body || "",
          hasMicroMission: Boolean(parsed.features?.microMission?.enabled),
          missionButtonLabel:
            parsed.features?.microMission?.buttonLabel || "Pasar a la acción",
          mission:
            parsed.features?.microMission?.mission || getDefaultMission(),
        };
      }

      if (parsed?.kind === "micro_mission_block") {
        return {
          body: parsed.visible?.text || "",
          hasMicroMission: true,
          missionButtonLabel:
            parsed.visible?.buttonLabel || "Pasar a la acción",
          mission: parsed.mission || getDefaultMission(),
        };
      }

      if (parsed?.kind === "micro_mission") {
        return {
          body: "Hoy vas a aplicar esta verdad en una acción concreta. No necesitas resolverlo todo. Solo necesitas dar un paso real.",
          hasMicroMission: true,
          missionButtonLabel: "Pasar a la acción",
          mission: parsed,
        };
      }
    } catch (err) {
      // Contenido normal antiguo: texto plano.
    }

    return {
      body: raw,
      hasMicroMission: false,
      missionButtonLabel: "Pasar a la acción",
      mission: getDefaultMission(),
    };
  }

  const orderedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const positionA = Number(a.position ?? 999999);
      const positionB = Number(b.position ?? 999999);

      if (positionA !== positionB) return positionA - positionB;

      return Number(a.id) - Number(b.id);
    });
  }, [items]);

  function handlePresetChange(value) {
    setPreset(value);

    const selected = BLOCK_PRESETS.find((p) => p.value === value);
    if (!selected) return;

    setType(selected.type);
    setTitle(selected.title);

    setBodyText("");
    setScriptureReference("");
    setScriptureText("");
    setApplicationText("");
    setClosingText("");
    setResourceNote("");
    setEmphasisStyle("bold");
    setLines([makeLine()]);

    setHasMicroMission(Boolean(selected.hasMicroMission));
    setMissionButtonLabel("Pasar a la acción");
    setShowMissionEditor(Boolean(selected.hasMicroMission));
    setMissionTitle("TOMA ACCIÓN");
    setMissionIntro(
      "Trabaja una decisión concreta a tu ritmo. Tus respuestas se guardarán en Bitácora.",
    );

    setMissionSteps([makeMissionStep(1)]);

    setMissionClosing({
      title: "Tu recorrido de hoy",
      message:
        "Tu trabajo quedó guardado. Puedes revisarlo cuando quieras en Bitácora.",
      blocks: [],
    });

    if (selected.value === "poder_ejecucion") {
      setBodyText("Hoy vas a aplicar esta verdad en una acción concreta.");

      setLines([
        makeLine({
          kind: "quote",
          icon: "none",
          text: "No necesitas resolverlo todo. Solo necesitas dar un paso real.",
        }),
      ]);
    }
  }

  function updateLine(lineId, patch) {
    setLines((prev) =>
      prev.map((line) => (line.id === lineId ? { ...line, ...patch } : line)),
    );
  }

  function addLine(afterId = null) {
    const newLine = makeLine();

    if (!afterId) {
      setLines((prev) => [...prev, newLine]);
      return;
    }

    setLines((prev) => {
      const index = prev.findIndex((line) => line.id === afterId);
      if (index === -1) return [...prev, newLine];

      const next = [...prev];
      next.splice(index + 1, 0, newLine);
      return next;
    });
  }

  function removeLine(lineId) {
    setLines((prev) => {
      const next = prev.filter((line) => line.id !== lineId);
      return next.length ? next : [makeLine()];
    });
  }

  function moveLine(lineId, direction) {
    setLines((prev) => {
      const index = prev.findIndex((line) => line.id === lineId);
      if (index === -1) return prev;

      const target = direction === "up" ? index - 1 : index + 1;
      if (target < 0 || target >= prev.length) return prev;

      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  function serializeLine(line, numberIndexRef) {
    const text = line.text.trim();
    if (!text) return "";

    const iconToken = line.icon !== "none" ? `[[ICON:${line.icon}]] ` : "";

    if (line.kind === "none") {
      return `${iconToken}${text}`.trim();
    }

    if (line.kind === "paragraph") {
      return `${iconToken}${text}`.trim();
    }

    if (line.kind === "quote") {
      return `[[QUOTE]] ${iconToken}${text}`.trim();
    }

    if (["dash", "dot", "check", "number"].includes(line.kind)) {
      if (line.kind === "number") numberIndexRef.current += 1;
      return `[[BULLET:${line.kind}]] ${iconToken}${text}`;
    }

    return `${iconToken}${text}`.trim();
  }

  function buildClosingLine(text, emphasis) {
    const clean = text.trim();
    if (!clean) return "";

    if (emphasis === "quote") return `[[QUOTE]] ${clean}`;
    if (emphasis === "bold") return `**${clean}**`;
    return clean;
  }

  function buildContentPayload() {
    const parts = [];
    const numberIndexRef = { current: 0 };

    const renderedLines = lines
      .map((line) => serializeLine(line, numberIndexRef))
      .filter(Boolean);

    if (preset === "momento_de_verdad") {
      if (scriptureReference.trim()) {
        parts.push(`[[SCRIPTURE_REFERENCE]] ${scriptureReference.trim()}`);
      }

      if (scriptureText.trim()) {
        parts.push(scriptureText.trim());
      }

      const appParts = [];

      if (applicationText.trim()) {
        appParts.push(applicationText.trim());
      }

      if (renderedLines.length) {
        appParts.push(...renderedLines);
      }

      const closing = buildClosingLine(closingText, emphasisStyle);
      if (closing) {
        appParts.push(closing);
      }

      if (appParts.length) {
        parts.push("---");
        parts.push(appParts.join("\n\n"));
      }
    } else {
      if (bodyText.trim()) {
        parts.push(bodyText.trim());
      }

      if (renderedLines.length) {
        parts.push(...renderedLines);
      }

      const closing = buildClosingLine(closingText, emphasisStyle);
      if (closing) {
        parts.push(closing);
      }

      if (preset === "vida_reino" && resourceNote.trim()) {
        parts.push(resourceNote.trim());
      }
    }

    const body = parts.join("\n\n").trim();

    if (!hasMicroMission) {
      return body;
    }

    return JSON.stringify(
      {
        kind: "lesson_block",
        version: 1,
        body,
        features: {
          microMission: {
            enabled: true,
            buttonLabel: missionButtonLabel.trim() || "Pasar a la acción",
            mission: {
              kind: "micro_mission",
              version: 2,
              title: missionTitle.trim() || "TOMA ACCIÓN",
              intro: missionIntro.trim(),
              steps: missionSteps,
              closing: {
                title: missionClosing.title?.trim() || "Tu recorrido de hoy",
                message:
                  missionClosing.message?.trim() ||
                  "Tu trabajo quedó guardado. Puedes revisarlo cuando quieras en Bitácora.",
              },
            },
          },
        },
      },
      null,
      2,
    );
  }

  const contentPreview = buildContentPayload();

  function getSelectedMediaUrl() {
    return selectedMedia?.url || manualMediaUrl.trim() || "";
  }

  function resetForm() {
    setEditingId(null);
    setEditingPosition(null);
    setPreset("custom");
    setType("text");
    setTitle("");
    setHasMicroMission(false);
    setMissionButtonLabel("Pasar a la acción");
    setShowMissionEditor(false);
    setMissionTitle("TOMA ACCIÓN");
    setMissionIntro(
      "Trabaja una decisión concreta a tu ritmo. Tus respuestas se guardarán en Bitácora.",
    );
    setBodyText("");
    setScriptureReference("");
    setScriptureText("");
    setApplicationText("");
    setClosingText("");
    setResourceNote("");
    setEmphasisStyle("bold");
    setLines([makeLine()]);

    setMissionSteps([makeMissionStep(1)]);

    setMissionClosing({
      title: "Tu recorrido de hoy",
      message:
        "Tu trabajo quedó guardado. Puedes revisarlo cuando quieras en Bitácora.",
      blocks: [],
    });

    setSelectedMedia(null);
    setManualMediaUrl("");
    setMediaSearch("");
  }

  async function createOrUpdateItem(e) {
    e.preventDefault();

    try {
      setSaving(true);

      const payload = {
        type,
        title: title.trim(),
        content: buildContentPayload(),
        media_url: getSelectedMediaUrl(),
        position: getPositionForPayload(),
      };

      if (editingId) {
        await apiFetch(
          `/api/systems/${systemId}/sections/${sectionId}/lessons/${lessonId}/items/${editingId}`,
          {
            method: "PUT",
            body: payload,
          },
        );
      } else {
        await apiFetch(
          `/api/systems/${systemId}/sections/${sectionId}/lessons/${lessonId}/items`,
          {
            method: "POST",
            body: payload,
          },
        );
      }

      resetForm();
      loadItems();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function removeItem(id) {
    if (!window.confirm("¿Eliminar bloque?")) return;

    try {
      await apiFetch(
        `/api/systems/${systemId}/sections/${sectionId}/lessons/${lessonId}/items/${id}`,
        {
          method: "DELETE",
        },
      );

      loadItems();
    } catch (err) {
      alert(err.message);
    }
  }

  function startEdit(item) {
    const inferredPreset = inferPresetFromTitle(item.title || "");
    const parsedBlock = parseLessonBlockContent(item.content || "");
    const parsed = parseExistingContent(parsedBlock.body || "", inferredPreset);

    setEditingId(item.id);
    setEditingPosition(item.position ?? null);
    setPreset(inferredPreset);
    setType(item.type === "micro_mission" ? "text" : item.type || "text");
    setTitle(item.title || "");

    setHasMicroMission(parsedBlock.hasMicroMission);
    setMissionButtonLabel(
      parsedBlock.missionButtonLabel || "Pasar a la acción",
    );
    setShowMissionEditor(false);

    setBodyText(parsed.bodyText || "");
    setScriptureReference(parsed.scriptureReference || "");
    setScriptureText(parsed.scriptureText || "");
    setApplicationText(parsed.applicationText || "");
    setClosingText(parsed.closingText || "");
    setResourceNote(parsed.resourceNote || "");
    setEmphasisStyle(parsed.emphasisStyle || "bold");
    setLines(parsed.lines?.length ? parsed.lines : [makeLine()]);

    const normalizedMission = normalizeMissionForEditor(parsedBlock.mission);
    setMissionTitle(normalizedMission.title);
    setMissionIntro(normalizedMission.intro);
    setMissionSteps(normalizedMission.steps);
    setMissionClosing({ ...normalizedMission.closing, blocks: [] });

    setSelectedMedia(
      item.media_url
        ? {
            title: item.title || "Media vinculada",
            url: item.media_url,
          }
        : null,
    );

    setManualMediaUrl(item.media_url || "");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  const showScripture = preset === "momento_de_verdad";
  const showApplicationText = preset === "momento_de_verdad";
  const showBodyText = preset !== "momento_de_verdad";
  const showResourceNote = preset === "vida_reino";

  function updateMissionStep(stepId, updates) {
    setMissionSteps((current) =>
      current.map((step) =>
        step.id === stepId ? { ...step, ...updates } : step,
      ),
    );
  }

  function addMissionStep() {
    setMissionSteps((current) => [
      ...current,
      makeMissionStep(current.length + 1),
    ]);
  }

  function removeMissionStep(stepId) {
    setMissionSteps((current) => {
      if (current.length <= 1) return current;

      return current
        .filter((step) => step.id !== stepId)
        .map((step, index) => ({
          ...step,
          number: index + 1,
          title: /^Paso \d+$/i.test(step.title || "")
            ? `Paso ${index + 1}`
            : step.title,
        }));
    });
  }

  function moveMissionStep(stepId, direction) {
    setMissionSteps((current) => {
      const index = current.findIndex((step) => step.id === stepId);
      const target = direction === "up" ? index - 1 : index + 1;

      if (index < 0 || target < 0 || target >= current.length) return current;

      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];

      return next.map((step, stepIndex) => ({
        ...step,
        number: stepIndex + 1,
      }));
    });
  }

  function addMissionBlock(stepId) {
    setMissionSteps((current) =>
      current.map((step) =>
        step.id === stepId
          ? {
              ...step,
              blocks: [...step.blocks, makeMissionBlock()],
            }
          : step,
      ),
    );
  }

  function updateMissionBlock(stepId, blockId, updates) {
    setMissionSteps((current) =>
      current.map((step) =>
        step.id === stepId
          ? {
              ...step,
              blocks: step.blocks.map((block) =>
                block.id === blockId ? { ...block, ...updates } : block,
              ),
            }
          : step,
      ),
    );
  }

  function updateMissionBlockOptions(stepId, blockId, text) {
    const options = String(text || "")
      .split(/\r?\n/)
      .map((label) => label.trim())
      .filter(Boolean)
      .map((label, index) => ({
        id: `${blockId}-option-${index + 1}`,
        value: label,
        label,
        description: "",
      }));

    updateMissionBlock(stepId, blockId, { options });
  }

  function removeMissionBlock(stepId, blockId) {
    setMissionSteps((current) =>
      current.map((step) =>
        step.id === stepId
          ? {
              ...step,
              blocks:
                step.blocks.length > 1
                  ? step.blocks.filter((block) => block.id !== blockId)
                  : step.blocks,
            }
          : step,
      ),
    );
  }

  function updateMissionClosing(updates) {
    setMissionClosing((current) => ({
      ...current,
      ...updates,
    }));
  }

  function updateMissionClosingBlock(blockId, updates) {
    setMissionClosing((current) => ({
      ...current,
      blocks: current.blocks.map((block) =>
        block.id === blockId ? { ...block, ...updates } : block,
      ),
    }));
  }

  function removeMissionClosingBlock(blockId) {
    setMissionClosing((current) => ({
      ...current,
      blocks:
        current.blocks.length > 1
          ? current.blocks.filter((block) => block.id !== blockId)
          : current.blocks,
    }));
  }

  return (
    <>
      <AdminHeader />

      <div style={{ padding: "26px 32px", maxWidth: 1100, margin: "0 auto" }}>
        <AdminPageHeader
          title="Contenido del día"
          subtitle="Editor guiado para construir la experiencia premium del miembro."
          backTo={`/admin/systems/${systemId}/sections/${sectionId}/lessons`}
        />

        <form onSubmit={createOrUpdateItem} style={formBox}>
          <div style={grid2}>
            <div>
              <label style={labelStyle}>Plantilla de bloque</label>
              <select
                value={preset}
                onChange={(e) => handlePresetChange(e.target.value)}
                style={inputStyle}
              >
                {BLOCK_PRESETS.map((presetOption) => (
                  <option key={presetOption.value} value={presetOption.value}>
                    {presetOption.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={labelStyle}>Tipo</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                style={inputStyle}
              >
                {BLOCK_TYPES.map((typeOption) => (
                  <option key={typeOption.value} value={typeOption.value}>
                    {typeOption.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label style={labelStyle}>Título visible del bloque</label>
            <input
              placeholder="Ej: 1. MOMENTOS DE VERDAD"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              style={inputStyle}
            />
          </div>

          <div style={editorSection}>
            <div style={sectionTitle}>Funcionalidad opcional</div>

            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                fontWeight: 700,
                color: "#334155",
              }}
            >
              <input
                type="checkbox"
                checked={hasMicroMission}
                onChange={(e) => {
                  setHasMicroMission(e.target.checked);
                  setShowMissionEditor(e.target.checked);
                }}
              />
              Agregar micro-misión a este bloque
            </label>

            {hasMicroMission && (
              <>
                <div>
                  <label style={labelStyle}>
                    Texto del botón de micro-misión
                  </label>
                  <input
                    placeholder="Ej: Pasar a la acción"
                    value={missionButtonLabel}
                    onChange={(e) => setMissionButtonLabel(e.target.value)}
                    style={inputStyle}
                  />
                </div>

                <button
                  type="button"
                  onClick={() => setShowMissionEditor((value) => !value)}
                  style={secondaryBtn}
                >
                  {showMissionEditor
                    ? "Ocultar configuración"
                    : "Configurar micro-misión"}
                </button>
              </>
            )}
          </div>

          {hasMicroMission && showMissionEditor && (
            <div style={editorSection}>
              <div style={sectionTitle}>
                TOMA ACCIÓN · Motor de experiencias
              </div>

              <div style={helperText}>
                Configura únicamente los pasos que necesita la jornada. Define
                cuáles respuestas son necesarias, qué alternativas honestas
                estarán disponibles y qué información se guardará en Bitácora.
              </div>

              <div style={grid2}>
                <div>
                  <label style={labelStyle}>Título de la experiencia</label>
                  <input
                    value={missionTitle}
                    onChange={(e) => setMissionTitle(e.target.value)}
                    placeholder="Ej: TOMA ACCIÓN"
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label style={labelStyle}>Introducción breve</label>
                  <textarea
                    value={missionIntro}
                    onChange={(e) => setMissionIntro(e.target.value)}
                    rows={3}
                    style={textareaStyle}
                  />
                </div>
              </div>

              <div style={{ display: "grid", gap: 18 }}>
                {missionSteps.map((step) => (
                  <div
                    key={step.id}
                    style={{
                      border: "1px solid #e6ebf1",
                      borderRadius: 18,
                      padding: 16,
                      background: "#ffffff",
                    }}
                  >
                    <div style={grid2}>
                      <div>
                        <label style={labelStyle}>Paso {step.number}</label>
                        <input
                          value={step.title}
                          onChange={(e) =>
                            updateMissionStep(step.id, {
                              title: e.target.value,
                            })
                          }
                          placeholder={`Título del paso ${step.number}`}
                          style={inputStyle}
                        />
                      </div>

                      <div>
                        <label style={labelStyle}>
                          Propósito del paso (opcional)
                        </label>
                        <input
                          value={step.description || ""}
                          onChange={(e) =>
                            updateMissionStep(step.id, {
                              description: e.target.value,
                            })
                          }
                          placeholder="Qué trabajará la persona en este paso"
                          style={inputStyle}
                        />
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        onClick={() => moveMissionStep(step.id, "up")}
                        disabled={step.number === 1}
                        style={miniBtn}
                      >
                        Subir paso
                      </button>
                      <button
                        type="button"
                        onClick={() => moveMissionStep(step.id, "down")}
                        disabled={step.number === missionSteps.length}
                        style={miniBtn}
                      >
                        Bajar paso
                      </button>
                      <button
                        type="button"
                        onClick={() => removeMissionStep(step.id)}
                        disabled={missionSteps.length === 1}
                        style={miniBtnDanger}
                      >
                        Eliminar paso
                      </button>
                    </div>

                    <div style={{ display: "grid", gap: 12, marginTop: 14 }}>
                      {step.blocks.map((block, index) => (
                        <div
                          key={block.id}
                          style={{
                            display: "grid",
                            gap: 10,
                            padding: 12,
                            borderRadius: 14,
                            background: "#f8fafc",
                            border: "1px solid #edf1f5",
                          }}
                        >
                          <div style={grid2}>
                            <div>
                              <label style={labelStyle}>
                                Tipo de bloque interno
                              </label>
                              <select
                                value={block.kind}
                                onChange={(e) =>
                                  updateMissionBlock(step.id, block.id, {
                                    kind: e.target.value,
                                  })
                                }
                                style={inputStyle}
                              >
                                <option value="text">Texto</option>
                                <option value="single_choice">
                                  Selección única
                                </option>
                                <option value="multi_choice">
                                  Selección múltiple
                                </option>
                                <option value="binary">
                                  Respuesta Sí / No
                                </option>
                                <option value="scale">
                                  Escala mediante botones
                                </option>
                                <option value="short_text">
                                  Respuesta breve
                                </option>
                                <option value="long_text">
                                  Reflexión escrita
                                </option>
                                <option value="complete_phrase">
                                  Frase para completar
                                </option>
                                <option value="confirmation">
                                  Confirmar una práctica
                                </option>
                                <option value="rule">Regla</option>
                                <option value="timer">Cronómetro</option>
                              </select>
                            </div>

                            <div>
                              <label style={labelStyle}>
                                Instrucción o ayuda
                              </label>
                              <input
                                value={block.instruction || ""}
                                onChange={(e) =>
                                  updateMissionBlock(step.id, block.id, {
                                    instruction: e.target.value,
                                  })
                                }
                                placeholder="Ej: Elige una sola opción"
                                style={inputStyle}
                              />
                            </div>
                          </div>

                          <textarea
                            value={block.prompt || ""}
                            onChange={(e) =>
                              updateMissionBlock(step.id, block.id, {
                                prompt: e.target.value,
                              })
                            }
                            rows={4}
                            placeholder={`Texto o pregunta del bloque ${index + 1}`}
                            style={textareaStyle}
                          />

                          {["single_choice", "multi_choice"].includes(
                            block.kind,
                          ) && (
                            <div>
                              <label style={labelStyle}>
                                Opciones · una por línea
                              </label>
                              <textarea
                                value={(block.options || [])
                                  .map((option) => option.label)
                                  .join("\n")}
                                onChange={(e) =>
                                  updateMissionBlockOptions(
                                    step.id,
                                    block.id,
                                    e.target.value,
                                  )
                                }
                                rows={6}
                                placeholder={
                                  "Primera opción\nSegunda opción\nTercera opción"
                                }
                                style={textareaStyle}
                              />
                            </div>
                          )}

                          {block.kind === "multi_choice" && (
                            <div>
                              <label style={labelStyle}>
                                Máximo de selecciones
                              </label>
                              <input
                                type="number"
                                min="0"
                                max="20"
                                value={block.maxSelections || 0}
                                onChange={(e) =>
                                  updateMissionBlock(step.id, block.id, {
                                    maxSelections: Number(e.target.value || 0),
                                  })
                                }
                                style={inputStyle}
                              />
                              <div style={helperText}>
                                Usa 0 cuando no exista límite.
                              </div>
                            </div>
                          )}

                          {block.kind === "scale" && (
                            <div style={grid2}>
                              <input
                                type="number"
                                value={block.min ?? 1}
                                onChange={(e) =>
                                  updateMissionBlock(step.id, block.id, {
                                    min: Number(e.target.value),
                                  })
                                }
                                placeholder="Mínimo"
                                style={inputStyle}
                              />
                              <input
                                type="number"
                                value={block.max ?? 5}
                                onChange={(e) =>
                                  updateMissionBlock(step.id, block.id, {
                                    max: Number(e.target.value),
                                  })
                                }
                                placeholder="Máximo"
                                style={inputStyle}
                              />
                              <input
                                value={block.minLabel || ""}
                                onChange={(e) =>
                                  updateMissionBlock(step.id, block.id, {
                                    minLabel: e.target.value,
                                  })
                                }
                                placeholder="Etiqueta del extremo inicial"
                                style={inputStyle}
                              />
                              <input
                                value={block.maxLabel || ""}
                                onChange={(e) =>
                                  updateMissionBlock(step.id, block.id, {
                                    maxLabel: e.target.value,
                                  })
                                }
                                placeholder="Etiqueta del extremo final"
                                style={inputStyle}
                              />
                            </div>
                          )}

                          {block.kind === "timer" && (
                            <div>
                              <label style={labelStyle}>
                                Duración en segundos
                              </label>
                              <input
                                type="number"
                                min="15"
                                max="1800"
                                value={block.seconds || 60}
                                onChange={(e) =>
                                  updateMissionBlock(step.id, block.id, {
                                    seconds: Number(e.target.value || 60),
                                  })
                                }
                                style={inputStyle}
                              />
                            </div>
                          )}

                          <div style={{ ...grid2, gap: 8 }}>
                            <label style={checkboxLabelStyle}>
                              <input
                                type="checkbox"
                                checked={Boolean(block.required)}
                                onChange={(e) =>
                                  updateMissionBlock(step.id, block.id, {
                                    required: e.target.checked,
                                  })
                                }
                              />
                              Respuesta necesaria
                            </label>

                            <label style={checkboxLabelStyle}>
                              <input
                                type="checkbox"
                                checked={Boolean(block.allowSkip)}
                                onChange={(e) =>
                                  updateMissionBlock(step.id, block.id, {
                                    allowSkip: e.target.checked,
                                  })
                                }
                              />
                              Permitir omitir por ahora
                            </label>

                            {[
                              "single_choice",
                              "multi_choice",
                              "binary",
                            ].includes(block.kind) && (
                              <label style={checkboxLabelStyle}>
                                <input
                                  type="checkbox"
                                  checked={Boolean(block.allowUnsure)}
                                  onChange={(e) =>
                                    updateMissionBlock(step.id, block.id, {
                                      allowUnsure: e.target.checked,
                                    })
                                  }
                                />
                                Añadir “Todavía no logro identificarlo”
                              </label>
                            )}

                            {["single_choice", "multi_choice"].includes(
                              block.kind,
                            ) && (
                              <label style={checkboxLabelStyle}>
                                <input
                                  type="checkbox"
                                  checked={Boolean(block.allowNone)}
                                  onChange={(e) =>
                                    updateMissionBlock(step.id, block.id, {
                                      allowNone: e.target.checked,
                                    })
                                  }
                                />
                                Añadir “Ninguna me representa”
                              </label>
                            )}

                            {block.kind === "single_choice" && (
                              <label style={checkboxLabelStyle}>
                                <input
                                  type="checkbox"
                                  checked={Boolean(block.allowOther)}
                                  onChange={(e) =>
                                    updateMissionBlock(step.id, block.id, {
                                      allowOther: e.target.checked,
                                    })
                                  }
                                />
                                Permitir respuesta propia
                              </label>
                            )}
                          </div>

                          <div>
                            <label style={labelStyle}>
                              Etiqueta en Bitácora
                            </label>
                            <input
                              value={block.journalLabel || ""}
                              onChange={(e) =>
                                updateMissionBlock(step.id, block.id, {
                                  journalLabel: e.target.value,
                                })
                              }
                              placeholder="Ej: Lo que reconociste"
                              style={inputStyle}
                            />
                          </div>

                          <div>
                            <button
                              type="button"
                              onClick={() =>
                                removeMissionBlock(step.id, block.id)
                              }
                              style={{
                                ...miniBtnDanger,
                                width: "fit-content",
                                padding: "8px 12px",
                                borderRadius: 10,
                                fontSize: 12,
                                fontWeight: 700,
                              }}
                            >
                              Eliminar bloque interno
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div style={{ marginTop: 12 }}>
                      <button
                        type="button"
                        onClick={() => addMissionBlock(step.id)}
                        style={secondaryBtn}
                      >
                        Añadir bloque interno
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={addMissionStep}
                style={{ ...secondaryBtn, marginTop: 14 }}
              >
                Añadir paso
              </button>

              <div
                style={{
                  marginTop: 22,
                  border: "1px solid #e6ebf1",
                  borderRadius: 18,
                  padding: 16,
                  background: "#fffaf0",
                }}
              >
                <div style={sectionTitle}>Cierre final</div>

                <input
                  value={missionClosing.title}
                  onChange={(e) =>
                    updateMissionClosing({ title: e.target.value })
                  }
                  placeholder="Ej: Tu recorrido de hoy"
                  style={inputStyle}
                />

                <textarea
                  value={missionClosing.message || ""}
                  onChange={(e) =>
                    updateMissionClosing({ message: e.target.value })
                  }
                  rows={3}
                  placeholder="Mensaje de confirmación después de guardar"
                  style={{ ...textareaStyle, marginTop: 12 }}
                />

                <div style={{ display: "grid", gap: 12, marginTop: 14 }}>
                  {missionClosing.blocks.map((block, index) => (
                    <div
                      key={block.id}
                      style={{
                        display: "grid",
                        gap: 10,
                        padding: 12,
                        borderRadius: 14,
                        background: "#ffffff",
                        border: "1px solid #edf1f5",
                      }}
                    >
                      <div style={grid2}>
                        <div>
                          <label style={labelStyle}>
                            Tipo de bloque interno
                          </label>
                          <select
                            value={block.kind}
                            onChange={(e) =>
                              updateMissionClosingBlock(block.id, {
                                kind: e.target.value,
                              })
                            }
                            style={inputStyle}
                          >
                            <option value="text">Texto</option>
                            <option value="question">Pregunta</option>
                            <option value="binary_question">
                              Pregunta binaria Sí / No
                            </option>
                            <option value="response">Campo de respuesta</option>
                            <option value="example">
                              Selección / opciones
                            </option>
                            <option value="choice_cards">
                              Tarjetas de elección
                            </option>
                            <option value="multi_select">
                              Selección múltiple
                            </option>
                            <option value="checklist">Checklist</option>
                            <option value="complete_phrase">
                              Frase para completar
                            </option>
                            <option value="rule">Regla</option>
                            <option value="timer">Cronómetro</option>
                          </select>
                        </div>

                        <div>
                          <label style={labelStyle}>Etiqueta opcional</label>
                          <input
                            value={block.placeholder || ""}
                            onChange={(e) =>
                              updateMissionClosingBlock(block.id, {
                                placeholder: e.target.value,
                              })
                            }
                            placeholder="Ej: Evidencia levantada hoy"
                            style={inputStyle}
                          />
                        </div>
                      </div>

                      <textarea
                        value={block.text}
                        onChange={(e) =>
                          updateMissionClosingBlock(block.id, {
                            text: e.target.value,
                          })
                        }
                        rows={4}
                        placeholder={`Contenido del cierre ${index + 1}`}
                        style={textareaStyle}
                      />

                      <div>
                        <button
                          type="button"
                          onClick={() => removeMissionClosingBlock(block.id)}
                          style={{
                            ...miniBtnDanger,
                            width: "fit-content",
                            padding: "8px 12px",
                            borderRadius: 10,
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                          Eliminar bloque interno
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {showScripture && (
            <div style={editorSection}>
              <div style={sectionTitle}>Palabra base</div>

              <input
                placeholder="Referencia bíblica"
                value={scriptureReference}
                onChange={(e) => setScriptureReference(e.target.value)}
                style={inputStyle}
              />

              <textarea
                placeholder="Texto bíblico"
                value={scriptureText}
                onChange={(e) => setScriptureText(e.target.value)}
                rows={6}
                style={textareaStyle}
              />
            </div>
          )}

          {showApplicationText && (
            <div style={editorSection}>
              <div style={sectionTitle}>Aplicación principal</div>

              <textarea
                placeholder="Guía, interpretación y dirección práctica"
                value={applicationText}
                onChange={(e) => setApplicationText(e.target.value)}
                rows={6}
                style={textareaStyle}
              />
            </div>
          )}

          {showBodyText && (
            <div style={editorSection}>
              <div style={sectionTitle}>Texto base</div>

              <textarea
                placeholder="Texto principal o introductorio"
                value={bodyText}
                onChange={(e) => setBodyText(e.target.value)}
                rows={6}
                style={textareaStyle}
              />
            </div>
          )}

          <div style={editorSection}>
            <div style={sectionTitle}>Líneas guiadas</div>
            <div style={helperText}>
              Cada fila puede tener su propio estilo: párrafo, bullet, check,
              numerada o frase destacada. También puedes elegir un ícono
              monocromo por línea y usar <strong>**negrita**</strong> dentro del
              texto.
            </div>

            <div style={{ display: "grid", gap: 10 }}>
              {lines.map((line, index) => (
                <div key={line.id} style={lineRow}>
                  <select
                    value={line.kind}
                    onChange={(e) =>
                      updateLine(line.id, { kind: e.target.value })
                    }
                    style={smallSelect}
                  >
                    {LINE_TYPES.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>

                  <select
                    value={line.icon}
                    onChange={(e) =>
                      updateLine(line.id, { icon: e.target.value })
                    }
                    style={smallSelect}
                  >
                    {ICON_STYLES.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>

                  <input
                    value={line.text}
                    onChange={(e) =>
                      updateLine(line.id, { text: e.target.value })
                    }
                    placeholder={`Línea ${index + 1}`}
                    style={{ ...inputStyle, margin: 0 }}
                  />

                  <div style={lineActions}>
                    <button
                      type="button"
                      onClick={() => moveLine(line.id, "up")}
                      style={miniBtn}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => moveLine(line.id, "down")}
                      style={miniBtn}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      onClick={() => addLine(line.id)}
                      style={miniBtn}
                    >
                      +
                    </button>
                    <button
                      type="button"
                      onClick={() => removeLine(line.id)}
                      style={miniBtnDanger}
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div>
              <button
                type="button"
                onClick={() => addLine()}
                style={secondaryBtn}
              >
                Añadir línea
              </button>
            </div>
          </div>

          <div style={editorSection}>
            <div style={grid2}>
              <div>
                <label style={labelStyle}>Estilo de frase final</label>
                <select
                  value={emphasisStyle}
                  onChange={(e) => setEmphasisStyle(e.target.value)}
                  style={inputStyle}
                >
                  {EMPHASIS_STYLES.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Frase de cierre</label>
                <input
                  placeholder="Opcional"
                  value={closingText}
                  onChange={(e) => setClosingText(e.target.value)}
                  style={inputStyle}
                />
              </div>
            </div>

            {showResourceNote && (
              <input
                placeholder="Nota opcional del recurso"
                value={resourceNote}
                onChange={(e) => setResourceNote(e.target.value)}
                style={inputStyle}
              />
            )}
          </div>

          {(type === "audio" || type === "video" || type === "download") && (
            <div style={editorSection}>
              <div style={sectionTitle}>Media vinculada</div>

              <input
                placeholder="Buscar media por nombre"
                value={mediaSearch}
                onChange={(e) => setMediaSearch(e.target.value)}
                style={inputStyle}
              />

              <div style={mediaGrid}>
                {filteredMedia
                  .slice(0, mediaSearch.trim() ? 24 : 8)
                  .map((item) => {
                    const active = selectedMedia?.url === item.url;

                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => {
                          setSelectedMedia(item);
                          setManualMediaUrl(item.url || "");
                        }}
                        style={{
                          ...mediaCard,
                          borderColor: active ? "#264763" : "#e6ebf1",
                          background: active ? "#f7fbff" : "#fff",
                        }}
                      >
                        <div style={mediaTitle}>{item.title}</div>
                        <div style={mediaMeta}>
                          {item.type || item.kind}{" "}
                          {item.provider ? `· ${item.provider}` : ""}
                        </div>
                      </button>
                    );
                  })}
              </div>

              <input
                placeholder="O pega manualmente la URL"
                value={manualMediaUrl}
                onChange={(e) => {
                  setManualMediaUrl(e.target.value);
                  setSelectedMedia(null);
                }}
                style={inputStyle}
              />
            </div>
          )}

          <div style={editorSection}>
            <div style={sectionTitle}>Vista previa del contenido guardado</div>
            <textarea
              value={contentPreview}
              readOnly
              rows={14}
              style={{ ...textareaStyle, background: "#f8fafc" }}
            />
          </div>

          <div style={actionRow}>
            <button style={primaryBtn} disabled={saving}>
              {saving
                ? "Guardando..."
                : editingId
                  ? "Actualizar bloque"
                  : "Crear bloque"}
            </button>

            {(editingId ||
              title ||
              bodyText ||
              scriptureText ||
              applicationText ||
              closingText) && (
              <button type="button" onClick={resetForm} style={secondaryBtn}>
                Limpiar
              </button>
            )}
          </div>
        </form>

        <div style={{ marginTop: 20, display: "grid", gap: 12 }}>
          {loading ? (
            <p style={{ color: "#6b7a88" }}>Cargando...</p>
          ) : items.length === 0 ? (
            <p style={{ color: "#6b7a88" }}>No hay bloques aún.</p>
          ) : (
            orderedItems.map((item) => {
              const parsedBlock = parseLessonBlockContent(item.content || "");

              return (
                <div key={item.id} style={cardStyle}>
                  <div style={{ minWidth: 0 }}>
                    <div style={itemTitle}>{item.title}</div>
                    <div style={itemMeta}>
                      Posición {item.position ?? "—"} · {item.type}
                      {parsedBlock.hasMicroMission
                        ? " · Micro-misión activa"
                        : ""}
                    </div>
                  </div>

                  <div style={actionsStyle}>
                    <button
                      type="button"
                      onClick={() => startEdit(item)}
                      style={secondaryBtn}
                    >
                      Editar bloque
                    </button>

                    <button
                      type="button"
                      onClick={() => removeItem(item.id)}
                      style={deleteBtn}
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </>
  );
}

/* ===== STYLES ===== */

const formBox = {
  display: "grid",
  gap: 12,
  padding: 16,
  borderRadius: 14,
  border: "1px solid #e6ebf1",
  background: "#fff",
  marginBottom: 16,
};

const grid2 = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 10,
};

const checkboxLabelStyle = {
  display: "flex",
  alignItems: "flex-start",
  gap: 8,
  padding: "10px 12px",
  border: "1px solid #e2e8f0",
  borderRadius: 10,
  background: "#ffffff",
  color: "#334155",
  fontSize: 13,
  fontWeight: 650,
};

const labelStyle = {
  display: "block",
  marginBottom: 5,
  fontSize: 14,
  fontWeight: 600,
  color: "#334155",
};

const inputStyle = {
  width: "100%",
  minHeight: 42,
  padding: "9px 11px",
  borderRadius: 9,
  border: "1px solid #d6dde5",
  background: "#fff",
  boxSizing: "border-box",
  fontSize: 14,
};

const textareaStyle = {
  width: "100%",
  padding: "10px 11px",
  borderRadius: 9,
  border: "1px solid #d6dde5",
  resize: "vertical",
  background: "#fff",
  boxSizing: "border-box",
  fontSize: 14,
  lineHeight: 1.5,
};

const editorSection = {
  display: "grid",
  gap: 8,
  padding: 12,
  borderRadius: 12,
  border: "1px solid #edf2f7",
  background: "#fcfdff",
};

const sectionTitle = {
  fontSize: 15,
  fontWeight: 700,
  color: "#0f2f44",
};

const helperText = {
  fontSize: 12,
  color: "#64748b",
  lineHeight: 1.45,
};

const lineRow = {
  display: "grid",
  gridTemplateColumns: "150px 140px 1fr auto",
  gap: 8,
  alignItems: "start",
};

const smallSelect = {
  width: "100%",
  minHeight: 40,
  padding: "9px 10px",
  borderRadius: 9,
  border: "1px solid #d6dde5",
  background: "#fff",
  boxSizing: "border-box",
  fontSize: 14,
};

const lineActions = {
  display: "flex",
  gap: 5,
  alignItems: "center",
};

const miniBtn = {
  width: 32,
  height: 32,
  borderRadius: 8,
  border: "1px solid #d6dde5",
  background: "#fff",
  cursor: "pointer",
  fontWeight: 700,
};

const miniBtnDanger = {
  width: 32,
  height: 32,
  borderRadius: 8,
  border: "1px solid #ead0d0",
  background: "#fff",
  cursor: "pointer",
  fontWeight: 700,
  color: "#9f3a38",
};

const mediaGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
  gap: 8,
};

const mediaCard = {
  border: "1px solid #e6ebf1",
  borderRadius: 10,
  padding: 10,
  textAlign: "left",
  cursor: "pointer",
  background: "#fff",
};

const mediaTitle = {
  fontSize: 14,
  fontWeight: 700,
  color: "#0f2f44",
  marginBottom: 3,
};

const mediaMeta = {
  fontSize: 12,
  color: "#6b7a88",
};

const actionRow = {
  display: "flex",
  gap: 8,
  alignItems: "center",
  flexWrap: "wrap",
};

const primaryBtn = {
  padding: "10px 14px",
  background: "#0a2230",
  color: "#fff",
  border: "none",
  borderRadius: 9,
  cursor: "pointer",
  fontWeight: 700,
  fontSize: 14,
};

const secondaryBtn = {
  border: "1px solid #d6dde5",
  padding: "9px 12px",
  borderRadius: 9,
  background: "#fff",
  cursor: "pointer",
  fontWeight: 600,
  fontSize: 14,
};

const cardStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 10,
  padding: 13,
  borderRadius: 12,
  border: "1px solid #e6ebf1",
  background: "#fff",
};

const itemTitle = {
  fontSize: 15,
  fontWeight: 700,
  color: "#0f2f44",
};

const itemMeta = {
  fontSize: 12,
  color: "#6b7a88",
  marginTop: 3,
};

const actionsStyle = {
  display: "flex",
  gap: 8,
  alignItems: "center",
  flexWrap: "wrap",
};

const deleteBtn = {
  border: "1px solid #ddd",
  padding: "9px 12px",
  borderRadius: 9,
  background: "#fff",
  cursor: "pointer",
  fontSize: 14,
};
